import api from './client';
import type {
  Produksjon,
  Bemanning,
  ProduksjonsPlan,
  PlanElement,
  TalentBehovItem,
  ProduksjonsKategoriTalentMal,
  Oppmote,
  VarslingsstatusRad,
} from './types';

/**
 * Produksjon API
 * Håndterer produksjoner, kategorier, plan-elementer, bemanning og talent-behov
 */
export const produksjonAPI = {
  // ===== PRODUKSJONER =====
  
  getAll: async (filters?: { 
    kategoriId?: number; 
    planId?: number; 
    publisert?: boolean;
    kommende?: boolean;
    gjennomfort?: boolean;
  }): Promise<{ produksjoner: Produksjon[] }> => {
    const params = new URLSearchParams();
    if (filters?.kategoriId) params.append('kategoriId', String(filters.kategoriId));
    if (filters?.planId) params.append('planId', String(filters.planId));
    if (filters?.publisert !== undefined) params.append('publisert', String(filters.publisert));
    if (filters?.kommende) params.append('kommende', 'true');
    if (filters?.gjennomfort) params.append('gjennomfort', 'true');
    
    const response = await api.get(`/produksjon?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<{ produksjon: Produksjon }> => {
    const response = await api.get(`/produksjon/${id}`);
    return response.data;
  },

  // Admin-visning: leveringsstatus for push-varsler per bemannet person (P3).
  // Se Docs/PUSH_ONLY_ARCHITECTURE.md §2.3.
  getVarslingsstatus: async (id: number): Promise<{ varslingsstatus: VarslingsstatusRad[] }> => {
    const response = await api.get(`/produksjon/${id}/varslingsstatus`);
    return response.data;
  },

  createProduksjon: async (data: {
    navn: string;
    tid: string;
    sluttTid?: string | null;
    kategoriId?: number;
    publisert?: boolean;
    beskrivelse?: string;
    planId?: number;
    applyKategoriMal?: boolean; // Kopier plan, talenter og oppmøtetider fra kategori-mal
    plassering?: string;
  }): Promise<{ produksjon: Produksjon }> => {
    const response = await api.post('/produksjon', data);
    return response.data;
  },

  updateProduksjon: async (id: number, data: {
    navn?: string;
    tid?: string;
    sluttTid?: string | null;
    kategoriId?: number;
    publisert?: boolean;
    beskrivelse?: string;
    planId?: number;
    plassering?: string;
    plassering_lat?: number | null;
    plassering_lng?: number | null;
    plassering_adresse?: string | null;
  }): Promise<{ produksjon: Produksjon }> => {
    const response = await api.put(`/produksjon/${id}`, data);
    return response.data;
  },

  // ===== BEMANNING =====
  
  getBemanning: async (id: number): Promise<{ bemanning: Bemanning[]; talentBehov: any[] }> => {
    const response = await api.get(`/produksjon/${id}/bemanning`);
    return response.data;
  },

  addBemanning: async (id: number, data: {
    personId: number;
    talentNavn: string;
    talentKategoriSti: string;
    notater?: string;
    status?: 'planlagt' | 'bekreftet' | 'avvist';
  }): Promise<{ bemanning: Bemanning }> => {
    const response = await api.post(`/produksjon/${id}/bemanning`, data);
    return response.data;
  },

  updateBemanning: async (id: number, bemanningId: number, data: {
    personId?: number;
    status?: 'planlagt' | 'bekreftet' | 'avvist';
    notater?: string;
    version?: number; // Optimistic locking - pass current version for conflict detection
  }): Promise<{ bemanning: Bemanning }> => {
    const response = await api.put(`/produksjon/${id}/bemanning/${bemanningId}`, data);
    return response.data;
  },

  removeBemanning: async (id: number, bemanningId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/produksjon/${id}/bemanning/${bemanningId}`);
    return response.data;
  },

  // ===== DRAFT OPERATIONS (Fase 2B) =====
  
  getDraftChanges: async (id: number): Promise<{ changes: any[] }> => {
    const response = await api.get(`/produksjon/${id}/draft-bemanning`);
    return response.data;
  },

  addPersonToDraft: async (id: number, data: {
    personId: number;
    talentNavn: string;
    talentKategoriSti: string;
    notater?: string;
    status?: 'planlagt' | 'bekreftet' | 'avvist';
  }): Promise<{ data: any }> => {
    const response = await api.post(`/produksjon/${id}/draft-bemanning`, data);
    return response.data;
  },

  removePersonFromDraft: async (id: number, personId: number, data: {
    talentNavn: string;
    talentKategoriSti: string;
  }): Promise<{ data: any }> => {
    const response = await api.delete(`/produksjon/${id}/draft-bemanning/${personId}`, { data });
    return response.data;
  },

  publishDraftChanges: async (id: number, changes: {
    added: any[];
    removed: any[];
    modified: any[];
  }): Promise<{ message: string }> => {
    const response = await api.post(`/produksjon/${id}/publish-draft`, { changes });
    return response.data;
  },

  cleanupDraftData: async (id: number): Promise<{ cleaned: number }> => {
    const response = await api.delete(`/produksjon/${id}/draft-bemanning`);
    return response.data;
  },

  // ===== TALENT-BEHOV =====
  
  getTalentBehov: async (id: number): Promise<{ talentBehov: TalentBehovItem[] }> => {
    const response = await api.get(`/produksjon/${id}/talent-behov`);
    return response.data;
  },

  addTalentBehov: async (id: number, data: { 
    talentNavn: string; 
    talentKategori: string; 
    antall: number; 
    beskrivelse?: string 
  }): Promise<{ talentBehov: TalentBehovItem }> => {
    const response = await api.post(`/produksjon/${id}/talent-behov`, data);
    return response.data;
  },

  updateTalentBehov: async (id: number, behovId: number, data: { 
    antall?: number; 
    beskrivelse?: string 
  }): Promise<{ talentBehov: TalentBehovItem }> => {
    const response = await api.put(`/produksjon/${id}/talent-behov/${behovId}`, data);
    return response.data;
  },

  removeTalentBehov: async (id: number, behovId: number): Promise<void> => {
    await api.delete(`/produksjon/${id}/talent-behov/${behovId}`);
  },

  // ===== OPPMØTETIDER (PRODUKSJON) =====
  
  getOppmote: async (id: number): Promise<{ oppmote: Oppmote[] }> => {
    const response = await api.get(`/produksjon/${id}/oppmote`);
    return response.data;
  },

  addOppmote: async (id: number, data: { 
    navn: string; 
    beskrivelse?: string; 
    tidspunkt?: string; // ISO 8601 - faktisk tid
    minutterFør?: number; // Alternativ: beregnes fra produksjon.tid
    rekkefølge?: number 
  }): Promise<{ oppmote: Oppmote }> => {
    const response = await api.post(`/produksjon/${id}/oppmote`, data);
    return response.data;
  },

  updateOppmote: async (id: number, oppmoteId: number, data: { 
    navn?: string; 
    beskrivelse?: string; 
    tidspunkt?: string; // ISO 8601 - faktisk tid
    minutterFør?: number; // Alternativ: beregnes fra produksjon.tid
    rekkefølge?: number 
  }): Promise<{ oppmote: Oppmote }> => {
    const response = await api.put(`/produksjon/${id}/oppmote/${oppmoteId}`, data);
    return response.data;
  },

  removeOppmote: async (id: number, oppmoteId: number): Promise<void> => {
    await api.delete(`/produksjon/${id}/oppmote/${oppmoteId}`);
  },

  // ===== PRODUKSJON OPPMØTE TALENT-KOBLING =====
  
  addTalentToOppmote: async (produksjonId: number, oppmoteId: number, data: {
    talentNavn: string;
    talentKategoriSti?: string;
    erGruppe?: boolean;
  }): Promise<{ talent: any }> => {
    const response = await api.post(`/produksjon/${produksjonId}/oppmote/${oppmoteId}/talents`, data);
    return response.data;
  },

  removeTalentFromOppmote: async (produksjonId: number, oppmoteId: number, talentNavn: string, talentKategoriSti?: string): Promise<void> => {
    const params = new URLSearchParams();
    if (talentKategoriSti) {
      params.append('talentKategoriSti', talentKategoriSti);
    }
    await api.delete(`/produksjon/${produksjonId}/oppmote/${oppmoteId}/talents/${encodeURIComponent(talentNavn)}${params.toString() ? `?${params.toString()}` : ''}`);
  },

  // ===== PLAN-ELEMENTER =====
  
  getPlan: async (id: number): Promise<{ plan: ProduksjonsPlan }> => {
    const response = await api.get(`/produksjon/planer/${id}`);
    return response.data;
  },

  getPlanElementer: async (produksjonId: number): Promise<{ planElementer: PlanElement[] }> => {
    const response = await api.get(`/produksjon/${produksjonId}/plan`);
    return response.data;
  },

  addPlanElement: async (produksjonId: number, data: {
    type: 'overskrift' | 'hendelse' | 'sang' | 'musikk_placeholder';
    navn: string;
    varighetMinutter?: number;
    parentId?: number;
    rekkefølge?: number;
    beskrivelse?: string;
    musikkId?: number;
    musikkTransponeringId?: number;
  }): Promise<{ element: PlanElement }> => {
    const response = await api.post(`/produksjon/${produksjonId}/plan`, data);
    return response.data;
  },

  updatePlanElement: async (produksjonId: number, elementId: number, data: {
    navn?: string;
    varighetMinutter?: number;
    rekkefølge?: number;
    beskrivelse?: string;
    parentId?: number;  // Tillat endring av parent (flytte hendelse mellom overskrifter)
  }): Promise<{ element: PlanElement }> => {
    const response = await api.put(`/produksjon/${produksjonId}/plan/${elementId}`, data);
    return response.data;
  },

  removePlanElement: async (produksjonId: number, elementId: number): Promise<void> => {
    await api.delete(`/produksjon/${produksjonId}/plan/${elementId}`);
  },

  // Konverter musikk-placeholder til sang
  convertPlaceholderToSang: async (produksjonId: number, elementId: number, data: {
    musikkId: number;
    musikkTransponeringId?: number | null;
    varighetMinutter?: number;
  }): Promise<{ element: PlanElement }> => {
    const response = await api.post(`/produksjon/${produksjonId}/plan/${elementId}/velg-sang`, data);
    return response.data;
  },

  // ===== KATEGORIER =====
  
  getAllKategorier: async (): Promise<{ kategorier: any[] }> => {
    const response = await api.get('/produksjon/kategorier');
    return response.data;
  },

  getKategori: async (id: number): Promise<{ kategori: any }> => {
    const response = await api.get(`/produksjon/kategorier/${id}`);
    return response.data;
  },

  createKategori: async (data: { 
    navn: string; 
    beskrivelse?: string; 
    plassering?: string 
  }): Promise<{ kategori: any }> => {
    const response = await api.post('/produksjon/kategorier', data);
    return response.data;
  },

  updateKategori: async (id: number, data: { 
    navn?: string; 
    beskrivelse?: string; 
    plassering?: string 
  }): Promise<{ kategori: any }> => {
    const response = await api.put(`/produksjon/kategorier/${id}`, data);
    return response.data;
  },

  deleteKategori: async (id: number, deep: boolean = false): Promise<{ message: string }> => {
    const url = deep ? `/produksjon/kategorier/${id}?deep=true` : `/produksjon/kategorier/${id}`;
    const response = await api.delete(url);
    return response.data;
  },

  // ===== KATEGORI MAL - TALENT =====
  
  getTalentMal: async (kategoriId: number): Promise<{ talentMal: ProduksjonsKategoriTalentMal[] }> => {
    const response = await api.get(`/produksjon/kategorier/${kategoriId}/talent-mal`);
    return response.data;
  },

  addTalentToMal: async (kategoriId: number, data: { 
    talentId: number; 
    antall?: number; 
    beskrivelse?: string 
  }): Promise<{ talentMal: ProduksjonsKategoriTalentMal }> => {
    const response = await api.post(`/produksjon/kategorier/${kategoriId}/talent-mal`, data);
    return response.data;
  },

  updateTalentInMal: async (kategoriId: number, malId: number, data: { 
    antall?: number; 
    beskrivelse?: string 
  }): Promise<{ talentMal: ProduksjonsKategoriTalentMal }> => {
    const response = await api.put(`/produksjon/kategorier/${kategoriId}/talent-mal/${malId}`, data);
    return response.data;
  },

  removeTalentFromMal: async (kategoriId: number, malId: number): Promise<void> => {
    await api.delete(`/produksjon/kategorier/${kategoriId}/talent-mal/${malId}`);
  },

  // ===== KATEGORI MAL - PLAN =====
  
  getPlanMal: async (kategoriId: number): Promise<{ planMal: any[] }> => {
    const response = await api.get(`/produksjon/kategorier/${kategoriId}/plan-mal`);
    return response.data;
  },

  addPlanMalElement: async (kategoriId: number, data: { 
    type: 'overskrift' | 'hendelse' | 'musikk_placeholder'; 
    navn: string; 
    varighetMinutter?: number; 
    parentId?: number; 
    rekkefølge?: number;
    beskrivelse?: string;
  }): Promise<{ element: any }> => {
    const response = await api.post(`/produksjon/kategorier/${kategoriId}/plan-mal`, data);
    return response.data;
  },

  updatePlanMalElement: async (kategoriId: number, elementId: number, data: { 
    navn?: string; 
    varighetMinutter?: number; 
    rekkefølge?: number;
    beskrivelse?: string;
    parentId?: number;
  }): Promise<{ element: any }> => {
    const response = await api.put(`/produksjon/kategorier/${kategoriId}/plan-mal/${elementId}`, data);
    return response.data;
  },

  updatePlanMalRekkefølge: async (kategoriId: number, elementId: number, rekkefølge: number): Promise<{ element: any }> => {
    const response = await api.patch(`/produksjon/kategorier/${kategoriId}/plan-mal/${elementId}/rekkefølge`, { rekkefølge });
    return response.data;
  },

  removePlanMalElement: async (kategoriId: number, elementId: number): Promise<void> => {
    await api.delete(`/produksjon/kategorier/${kategoriId}/plan-mal/${elementId}`);
  },

  // ===== KATEGORI MAL - OPPMØTE =====
  
  getOppmoteMal: async (kategoriId: number): Promise<{ oppmoteMal: any[] }> => {
    const response = await api.get(`/produksjon/kategorier/${kategoriId}/oppmote-mal`);
    return response.data;
  },

  addOppmoteToMal: async (kategoriId: number, data: {
    navn: string;
    beskrivelse?: string;
    minutterFørStart?: number;
    rekkefølge?: number;
  }): Promise<{ oppmote: any }> => {
    const response = await api.post(`/produksjon/kategorier/${kategoriId}/oppmote-mal`, data);
    return response.data;
  },

  updateOppmoteInMal: async (kategoriId: number, oppmoteId: number, data: {
    navn?: string;
    beskrivelse?: string;
    minutterFørStart?: number;
    rekkefølge?: number;
  }): Promise<{ oppmote: any }> => {
    const response = await api.put(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}`, data);
    return response.data;
  },

  updateOppmoteRekkefølge: async (kategoriId: number, oppmoteId: number, rekkefølge: number): Promise<{ oppmote: any }> => {
    const response = await api.patch(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}/rekkefølge`, { rekkefølge });
    return response.data;
  },

  removeOppmoteFromMal: async (kategoriId: number, oppmoteId: number): Promise<void> => {
    await api.delete(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}`);
  },

  // ===== KATEGORI MAL - OPPMØTE TALENT-KOBLING =====
  
  getOppmoteMalTalents: async (kategoriId: number, oppmoteId: number): Promise<{ talents: any[] }> => {
    // Note: Backend returnerer talents direkte i oppmote-mal response
    // Denne funksjonen kan brukes hvis vi trenger separat endpoint
    const response = await api.get(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}/talents`);
    return response.data;
  },

  addTalentToOppmoteMal: async (kategoriId: number, oppmoteId: number, data: {
    talentId?: number;
    talentKategoriId?: number;
  }): Promise<{ talent: any }> => {
    const response = await api.post(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}/talents`, data);
    return response.data;
  },

  removeTalentFromOppmoteMal: async (kategoriId: number, oppmoteId: number, data: {
    talentId?: number;
    talentKategoriId?: number;
  }): Promise<void> => {
    await api.delete(`/produksjon/kategorier/${kategoriId}/oppmote-mal/${oppmoteId}/talents`, { data });
  },

  // ===== KOMPLETT MAL =====
  
  getKomplettMal: async (kategoriId: number): Promise<{
    kategori: any;
    talentMal: any[];
    planMal: any[];
    oppmoteMal: any[];
  }> => {
    const response = await api.get(`/produksjon/kategorier/${kategoriId}/komplett-mal`);
    return response.data;
  },
};

